import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, UnstyledButton } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDeleteLeft, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { NumericKeypad } from '../../../../../../components/Keypad/NumericKeypad';
import { Button } from '../../../../../../components/Button';
import { PosWheelTimePicker } from '../../PosWheelTimePicker';
import type { CategoryView } from '../../../../../../services/categories/categories.service';
import { happyHourService, type HappyHourView } from '../../../../../../services/happy-hour/happy-hour.service';
import { toHebrewApiErrorMessage } from '../../../../../../services/index/index.service';
import navyModal from '../../../../../../components/Modal/navy-modal.module.scss';
import classes from './happy-hour-setup-modal.module.scss';

type HappyHourSetupModalProps = {
  opened: boolean;
  onClose: () => void;
  tenantId: string;
  employeeSession: string | null;
  categories: CategoryView[];
  happyHour: HappyHourView | null;
};

const DAY_OPTIONS = [
  { value: 0, label: 'א' },
  { value: 1, label: 'ב' },
  { value: 2, label: 'ג' },
  { value: 3, label: 'ד' },
  { value: 4, label: 'ה' },
  { value: 5, label: 'ו' },
  { value: 6, label: 'ש' },
];

const TIME_24H_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

function isValidTime24(value: string): boolean {
  return TIME_24H_RE.test(value);
}

function normalizePercent(buffer: string): number {
  const parsed = Number(buffer);
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.min(100, Math.max(0, Math.trunc(parsed)));
}

export const HappyHourSetupModal: React.FC<HappyHourSetupModalProps> = ({
  opened,
  onClose,
  tenantId,
  employeeSession,
  categories,
  happyHour,
}) => {
  const saveInFlightRef = useRef(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [startTime, setStartTime] = useState('17:00');
  const [endTime, setEndTime] = useState('20:00');
  const [openTimePicker, setOpenTimePicker] = useState<'start' | 'end' | null>(null);
  const [activeDays, setActiveDays] = useState<number[]>([]);
  const [discountBuffer, setDiscountBuffer] = useState('50');
  const [isSaving, setIsSaving] = useState(false);

  const activeCategories = useMemo(
    () => categories.filter((category) => category.isActive).sort((a, b) => a.sortOrder - b.sortOrder),
    [categories],
  );
  const discountPct = normalizePercent(discountBuffer);
  const hasValidTimeRange = isValidTime24(startTime) && isValidTime24(endTime) && startTime < endTime;
  const canContinue = selectedCategoryIds.length > 0;
  const canConfirm =
    selectedCategoryIds.length > 0 &&
    hasValidTimeRange &&
    activeDays.length > 0 &&
    discountPct > 0 &&
    discountPct <= 100 &&
    !isSaving;

  useEffect(() => {
    if (!opened) {
      return;
    }

    setStep(1);
    setSelectedCategoryIds([]);
    setStartTime(happyHour?.startTime ?? '17:00');
    setEndTime(happyHour?.endTime ?? '20:00');
    setOpenTimePicker(null);
    setActiveDays(happyHour?.activeDays ?? []);
    setDiscountBuffer(String(happyHour?.discountPct ?? 50));
    setIsSaving(false);
    saveInFlightRef.current = false;
  }, [happyHour, opened]);

  const toggleCategory = (categoryId: number) => {
    if (isSaving) {
      return;
    }
    setSelectedCategoryIds((previous) =>
      previous.includes(categoryId)
        ? previous.filter((id) => id !== categoryId)
        : [...previous, categoryId],
    );
  };

  const toggleDay = (day: number) => {
    if (isSaving) {
      return;
    }
    setActiveDays((previous) =>
      previous.includes(day)
        ? previous.filter((value) => value !== day)
        : [...previous, day].sort((a, b) => a - b),
    );
  };

  const pressDigit = (digit: string) => {
    if (isSaving) {
      return;
    }
    setDiscountBuffer((previous) => {
      const base = previous === '0' ? '' : previous;
      const next = `${base}${digit}`.slice(0, 3);
      const normalized = normalizePercent(next);
      return normalized === 100 ? '100' : String(normalized);
    });
  };

  const pressBackspace = () => {
    if (isSaving) {
      return;
    }
    setDiscountBuffer((previous) => previous.slice(0, -1));
  };

  const handleConfirm = async () => {
    if (!employeeSession || !canConfirm || saveInFlightRef.current) {
      return;
    }
    saveInFlightRef.current = true;
    setIsSaving(true);
    try {
      await happyHourService.activate(
        tenantId,
        {
          categoryIds: selectedCategoryIds,
          startTime,
          endTime,
          activeDays,
          discountPct,
        },
        employeeSession,
      );
      onClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      notifications.show({
        color: 'red',
        title: 'שגיאה',
        message: toHebrewApiErrorMessage(message),
        autoClose: 3000,
      });
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);
    }
  };

  const renderTimeField = (
    key: 'start' | 'end',
    label: string,
    value: string,
    setValue: (next: string) => void,
  ) => (
    <span className={classes.timeField}>
      <UnstyledButton
        type="button"
        data-testid={`happy-hour-time-${key}`}
        className={classes.timeFieldButton}
        onClick={() => setOpenTimePicker((previous) => (previous === key ? null : key))}
        disabled={isSaving}
        aria-label={label}
      >
        {value}
      </UnstyledButton>
      {openTimePicker === key && (
        <span className={classes.timePickerDropdown} role="dialog" aria-label="בחירת שעה">
          <PosWheelTimePicker value={value} onChange={setValue} disabled={isSaving} />
        </span>
      )}
    </span>
  );

  return (
    <Modal
      opened={opened}
      onClose={isSaving ? () => undefined : onClose}
      onContextMenu={(event) => event.preventDefault()}
      withCloseButton={false}
      centered
      size={525}
      radius="lg"
      overlayProps={{ opacity: 0.55, blur: 1 }}
      closeOnClickOutside={!isSaving}
      closeOnEscape={!isSaving}
      classNames={{ root: navyModal.noLongPress, content: classes.modalContent, body: classes.modalBody }}
    >
      <div className={classes.modalInner}>
        <header className={classes.header}>
          <UnstyledButton
            type="button"
            data-testid="happy-hour-close"
            className={classes.closeButton}
            onClick={onClose}
            disabled={isSaving}
            aria-label="סגור"
            title="סגור"
          >
            <FontAwesomeIcon icon={faXmark as IconProp} />
          </UnstyledButton>
          <h2 className={classes.title}>Happy Hour 🎉</h2>
        </header>

        <div className={classes.progress} aria-hidden="true">
          <span className={classes.progressPillActive} />
          <span className={step === 2 ? classes.progressPillActive : classes.progressPill} />
        </div>

        {step === 1 ? (
          <section className={classes.stepBody}>
            <p className={classes.instruction}>בחר קטגוריות שיקבלו הנחת Happy Hour</p>
            <div className={classes.categoryGrid}>
              {activeCategories.map((category) => {
                const selected = selectedCategoryIds.includes(category.id);
                return (
                  <UnstyledButton
                    key={category.id}
                    type="button"
                    data-testid={`happy-hour-category-${category.id}`}
                    className={`${classes.categoryChip} ${selected ? classes.categoryChipSelected : ''}`}
                    onClick={() => toggleCategory(category.id)}
                    disabled={isSaving}
                  >
                    {category.name}
                  </UnstyledButton>
                );
              })}
            </div>
          </section>
        ) : (
          <section className={classes.stepBody}>
          <div className={classes.timeGrid}>
            <div className={classes.fieldLabel}>
              <span>שעת התחלה</span>
              {renderTimeField('start', 'שעת התחלה', startTime, setStartTime)}
            </div>
            <span className={classes.timeSeparator}>-</span>
            <div className={classes.fieldLabel}>
              <span>שעת סיום</span>
              {renderTimeField('end', 'שעת סיום', endTime, setEndTime)}
            </div>
          </div>

          <div className={classes.divider} />

          <div className={classes.daysSection}>
            <p className={classes.sectionLabel}>ימים פעילים</p>
            <div className={classes.dayRow}>
              {DAY_OPTIONS.map((day) => {
                const selected = activeDays.includes(day.value);
                return (
                  <UnstyledButton
                    key={day.value}
                    type="button"
                    data-testid={`happy-hour-day-${day.value}`}
                    className={`${classes.dayButton} ${selected ? classes.dayButtonSelected : ''}`}
                    onClick={() => toggleDay(day.value)}
                    disabled={isSaving}
                  >
                    {day.label}
                  </UnstyledButton>
                );
              })}
            </div>
          </div>

          <div className={classes.divider} />

          <div className={classes.discountSection}>
            <p className={classes.sectionLabel}>אחוז הנחה</p>
            <div className={classes.discountDisplay}>
              <span>{discountBuffer || '0'}</span>
              <strong>%</strong>
            </div>
            <NumericKeypad
              dataTestId="happy-hour-discount"
              keypadClassName={classes.keypad}
              keyClassName={classes.key}
              disabled={isSaving}
              onDigit={pressDigit}
              onBackspace={pressBackspace}
              backspaceDisabled={discountBuffer.length === 0}
              backspaceAriaLabel="מחק ספרה"
              backspaceIcon={<FontAwesomeIcon icon={faDeleteLeft as IconProp} />}
              backspaceClassName={`${classes.key} ${classes.backspaceKey}`}
              leftKey={{
                label: '',
                disabled: true,
                ariaHidden: true,
                tabIndex: -1,
                className: `${classes.key} ${classes.keyBlank}`,
              }}
            />
          </div>
          </section>
        )}

        <footer className={classes.footer}>
          {step === 1 ? (
            <>
              <Button variant="save" fullWidth onClick={() => setStep(2)} disabled={!canContinue || isSaving} dataTestId="happy-hour-next">
                הבא
              </Button>
              <Button variant="cancel" fullWidth onClick={onClose} disabled={isSaving} dataTestId="happy-hour-cancel">
                ביטול
              </Button>
            </>
          ) : (
            <>
              <Button variant="save" fullWidth onClick={handleConfirm} disabled={!canConfirm} loading={isSaving} dataTestId="happy-hour-confirm">
                אישור
              </Button>
              <Button variant="cancel" fullWidth onClick={() => setStep(1)} disabled={isSaving} dataTestId="happy-hour-back">
                חזור ›
              </Button>
            </>
          )}
        </footer>

        {openTimePicker !== null && (
          <UnstyledButton
            type="button"
            data-testid="happy-hour-time-backdrop"
            className={classes.timePickerBackdrop}
            aria-label="סגור בחירת שעה"
            onClick={() => setOpenTimePicker(null)}
          />
        )}
      </div>
    </Modal>
  );
};
